import { Trans, useTranslation } from "react-i18next";
import type { FacilityNode } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useShowError } from "../../error/ErrorProvider.js";
import { useMoveFacility } from "./useMoveFacility.js";

export interface MoveConfirmDialogProps {
  /** The dragged facility. */
  source: FacilityNode;
  /** The drop target (new parent) — already validated client-side by `canDrop` in the drag handler. */
  target: FacilityNode;
  session: AuthSession | null;
  onClose: () => void;
  /** Announce a successful move to the parent's aria-live region. */
  onMoved?: (sourceName: string, destName: string) => void;
}

/**
 * The drag-and-drop move confirm. A pointer drop has already picked a valid target (`canDrop`-gated in `onDragEnd`),
 * so this is a plain "are you sure?" over the reusable {@link ConfirmDialog} — the richer picker is the drawer
 * ({@link MoveFacilityDrawer}), reached from the kebab. A residual server rejection (403/409 the client couldn't
 * foresee) routes to the shared error dialog; the tree query refetches on success.
 */
export function MoveConfirmDialog({ source, target, session, onClose, onMoved }: MoveConfirmDialogProps) {
  const { t } = useTranslation();
  const move = useMoveFacility(session);
  const showError = useShowError();
  return (
    <ConfirmDialog
      isOpen
      title={t("facilityManagement.move.title")}
      message={
        <Trans
          i18nKey="facilityManagement.move.prompt"
          components={{
            name: <strong className="text-foreground">{source.name}</strong>,
            target: <strong className="text-foreground">{target.name}</strong>,
          }}
        />
      }
      confirmLabel={t("facilityManagement.move.confirm")}
      isPending={move.isPending}
      onConfirm={() =>
        move.mutate(
          { id: source.id, newParentId: target.id },
          {
            onSuccess: () => {
              onMoved?.(source.name, target.name);
              onClose();
            },
            onError: (err) => {
              showError(err);
              onClose();
            },
          },
        )
      }
      onCancel={onClose}
    />
  );
}
